import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, authFetch } from "../api/client.ts";
import { Upload, FileText, CheckCircle2, AlertCircle, Loader2, Bot, Info } from "lucide-react";

type ImportFormat = "ris" | "bibtex" | "csv";
type ImportSource = "PUBMED" | "EMBASE" | "CINAHL" | "SCOPUS" | "WEB_OF_SCIENCE" | "IEEE" | "COCHRANE" | "MANUAL" | "OTHER";

const SOURCE_LABELS: Record<ImportSource, string> = {
  PUBMED: "PubMed",
  EMBASE: "Embase",
  CINAHL: "CINAHL",
  SCOPUS: "Scopus",
  WEB_OF_SCIENCE: "Web of Science",
  IEEE: "IEEE Xplore",
  COCHRANE: "Cochrane Library",
  MANUAL: "Manual entry",
  OTHER: "Other",
};

interface ReviewOption {
  id: string;
  name: string;
  type: string;
}

interface ImportResult {
  imported: number;
  duplicates: number;
  errors: number;
  total: number;
  importedStudyIds: string[];
}

type BatchAiResult =
  | { queued: true; jobId: string; total: number }
  | { queued?: false; processed: number; errors: number; total: number };

export default function ImportPage() {
  const qc = useQueryClient();
  const [format, setFormat] = useState<ImportFormat>("ris");
  const [source, setSource] = useState<ImportSource>("PUBMED");
  const [reviewId, setReviewId] = useState("");
  const [runBatchAi, setRunBatchAi] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [batchAiResult, setBatchAiResult] = useState<BatchAiResult | null>(null);
  const [batchAiRunning, setBatchAiRunning] = useState(false);
  const [batchAiProgress, setBatchAiProgress] = useState<{ processed: number; total: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  function acceptFile(file: File | null) {
    setSelectedFile(file);
    setResult(null);
    setBatchAiResult(null);
    setBatchAiProgress(null);
    setError(null);
  }

  // Polls a background batch-AI job instead of leaving the coordinator with
  // just "running in the background" and an email that may not arrive (see
  // the same pattern for database search in AdminPage.tsx). Updates the
  // dashboard/review stat queries on completion so counts refresh without a
  // manual page reload.
  const pollBatchAi = async (jobId: string) => {
    const POLL_INTERVAL_MS = 2000;
    const MAX_POLLS = 300; // 10 minutes; a large backlog can take a while

    for (let attempt = 0; attempt < MAX_POLLS; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

      const status = await api.get<
        | { state: "not_found" }
        | { state: "pending"; progress: { processed: number; total: number } | null }
        | { state: "completed"; result: { totalProcessed: number; totalErrors: number } }
        | { state: "failed"; error: string }
      >(`/screening/batch-ai-predict/${jobId}`);

      if (status.state === "completed") {
        setBatchAiResult({
          processed: status.result.totalProcessed,
          errors: status.result.totalErrors,
          total: status.result.totalProcessed + status.result.totalErrors,
        });
        setBatchAiProgress(null);
        qc.invalidateQueries({ queryKey: ["review-stats"] });
        qc.invalidateQueries({ queryKey: ["study-stats"] });
        qc.invalidateQueries({ queryKey: ["admin-reviews"] });
        return;
      }
      if (status.state === "failed") {
        setError(`AI screening failed: ${status.error}`);
        setBatchAiProgress(null);
        return;
      }
      if (status.state === "not_found") {
        setBatchAiProgress(null);
        return;
      }
      if (status.progress) setBatchAiProgress(status.progress);
    }
  };

  // Load reviews from API so the list is always up-to-date
  const { data: reviewsData } = useQuery({
    queryKey: ["reviews-for-import"],
    queryFn: () => api.get<{ reviews: ReviewOption[] }>("/admin/reviews"),
  });
  const reviews = reviewsData?.reviews ?? [];

  const handleImport = async () => {
    const file = selectedFile;
    if (!file) { setError("Please select or drop a file."); return; }

    setLoading(true);
    setError(null);
    setResult(null);
    setBatchAiResult(null);

    const formData = new FormData();
    formData.append("file", file);

    const params = new URLSearchParams({ source });
    if (reviewId) params.set("reviewId", reviewId);

    try {
      const response = await authFetch(`/api/import/${format}?${params.toString()}`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const body = await response.json() as { error?: string };
        throw new Error(body.error ?? "Import failed");
      }

      const importResult = await response.json() as ImportResult;
      setResult(importResult);

      // Batch AI screening — runs when toggle is on, with or without a specific review
      if (runBatchAi && importResult.importedStudyIds.length > 0) {
        setBatchAiRunning(true);
        setBatchAiProgress(null);
        try {
          const aiResult = await api.post<BatchAiResult>("/screening/batch-ai-predict", {
            ...(reviewId ? { reviewId } : {}),
            studyIds: importResult.importedStudyIds,
            phase: "TITLE_ABSTRACT",
          });
          setBatchAiResult(aiResult);
          if (aiResult.queued) await pollBatchAi(aiResult.jobId);
        } catch (aiErr) {
          // Non-fatal — import succeeded even if batch AI fails
          console.warn("Batch AI screening failed:", aiErr);
          setBatchAiResult({ processed: 0, errors: importResult.importedStudyIds.length, total: importResult.importedStudyIds.length });
        } finally {
          setBatchAiRunning(false);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const canRunBatchAi = runBatchAi;

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Import Studies</h1>

      <div className="border rounded-lg p-6 bg-card space-y-5">
        <div>
          <label className="block text-sm font-medium mb-1.5">File format</label>
          <div className="flex gap-3">
            {(["ris", "bibtex", "csv"] as ImportFormat[]).map((f) => (
              <button
                key={f}
                onClick={() => { setFormat(f); acceptFile(null); if (fileRef.current) fileRef.current.value = ""; }}
                className={`px-4 py-2 rounded-md text-sm border transition-colors ${
                  format === f ? "bg-primary text-primary-foreground border-primary" : "hover:bg-accent"
                }`}
              >
                {f.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5">Database source</label>
          <select
            value={source}
            onChange={(e) => setSource(e.target.value as ImportSource)}
            className="border rounded-md px-3 py-2 text-sm bg-background w-full"
          >
            {(Object.keys(SOURCE_LABELS) as ImportSource[]).map((s) => (
              <option key={s} value={s}>{SOURCE_LABELS[s]}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5">Assign to review (optional)</label>
          <select
            value={reviewId}
            onChange={(e) => setReviewId(e.target.value)}
            className="border rounded-md px-3 py-2 text-sm bg-background w-full"
          >
            <option value="">(shared pool, no specific review)</option>
            {reviews.map((r) => (
              <option key={r.id} value={r.id}>{r.name} ({r.type})</option>
            ))}
          </select>
          {runBatchAi && !reviewId && (
            <p className="text-xs text-primary mt-1 flex items-center gap-1">
              <Info className="h-3 w-3" /> AI will screen against all reviews and assign automatically.
            </p>
          )}
        </div>

        {/* Batch AI toggle */}
        <div className={`rounded-lg border p-3 ${canRunBatchAi ? "border-primary/40 bg-primary/5" : "bg-secondary/30"}`}>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={runBatchAi}
              onChange={(e) => setRunBatchAi(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <div>
              <div className="flex items-center gap-2 text-sm font-medium">
                <Bot className="h-4 w-4 text-primary" />
                Pre-run AI title/abstract screening after import
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                For every imported study, the AI will predict INCLUDE / EXCLUDE / UNCERTAIN based on title
                and abstract. When no specific review is selected, the AI screens against all reviews and
                automatically assigns each paper to whichever ones it finds relevant. A single paper may
                land in multiple reviews. Reviewers see suggestions when they open the screening queue.
              </p>
            </div>
          </label>
          {canRunBatchAi && reviewId && (
            <p className="text-xs text-primary mt-2 ml-7">
              ✓ Will screen all newly imported studies against <strong>{reviews.find(r => r.id === reviewId)?.name}</strong> only.
            </p>
          )}
          {canRunBatchAi && !reviewId && (
            <p className="text-xs text-primary mt-2 ml-7">
              ✓ Will screen all newly imported studies against every active review and auto-assign.
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5">File</label>
          <div
            className={`border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors ${
              isDragging
                ? "border-primary bg-primary/10"
                : selectedFile
                ? "border-primary/50 bg-primary/5"
                : "hover:bg-accent/50"
            }`}
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragging(false); }}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              const file = e.dataTransfer.files[0] ?? null;
              acceptFile(file ?? null);
            }}
          >
            {selectedFile ? (
              <>
                <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-primary" />
                <p className="text-sm font-medium text-primary">{selectedFile.name}</p>
                <p className="text-xs text-muted-foreground mt-1">Click or drop a file to replace</p>
              </>
            ) : isDragging ? (
              <>
                <Upload className="h-8 w-8 mx-auto mb-2 text-primary animate-bounce" />
                <p className="text-sm font-medium text-primary">Drop to select</p>
              </>
            ) : (
              <>
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Drag &amp; drop or click to select a .{format === "bibtex" ? "bib" : format} file
                </p>
              </>
            )}
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              accept={format === "ris" ? ".ris" : format === "bibtex" ? ".bib,.bibtex" : ".csv"}
              onChange={(e) => acceptFile(e.target.files?.[0] ?? null)}
            />
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-destructive text-sm">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        {result && (
          <div className="p-4 rounded-md bg-secondary text-sm space-y-2">
            <div className="flex items-center gap-2 font-semibold text-green-700 mb-2">
              <CheckCircle2 className="h-4 w-4" />
              Import complete
            </div>
            <div className="grid grid-cols-2 gap-1 text-muted-foreground">
              <span>Total processed:</span><span className="font-medium text-foreground">{result.total}</span>
              <span>Imported:</span><span className="font-medium text-green-700">{result.imported}</span>
              <span>Duplicates skipped:</span><span className="font-medium text-amber-700">{result.duplicates}</span>
              <span>Errors:</span><span className="font-medium text-destructive">{result.errors}</span>
            </div>

            {/* Batch AI result */}
            {batchAiRunning && !batchAiResult && (
              <div className="flex items-center gap-2 pt-2 border-t text-primary text-xs">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Starting AI screening for {result.importedStudyIds.length} studies…
              </div>
            )}
            {batchAiResult && (
              <div className="pt-2 border-t text-xs">
                {batchAiResult.queued ? (
                  <>
                    <div className="flex items-center gap-2 font-semibold text-primary mb-1">
                      {batchAiRunning
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <Bot className="h-3.5 w-3.5" />}
                      {batchAiRunning ? "AI screening running…" : "AI screening finished"}
                    </div>
                    {batchAiProgress && batchAiProgress.total > 0 && (
                      <div className="mb-1.5">
                        <div className="h-1.5 w-full rounded-full bg-secondary overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all duration-300"
                            style={{
                              width: `${Math.min(100, Math.round((batchAiProgress.processed / batchAiProgress.total) * 100))}%`,
                            }}
                          />
                        </div>
                        <p className="text-muted-foreground mt-0.5">
                          {batchAiProgress.processed} of {batchAiProgress.total} screened so far
                        </p>
                      </div>
                    )}
                    <p className="text-muted-foreground">
                      {batchAiRunning
                        ? `${batchAiResult.total} studies are being screened on the server. You can carry on using the platform; this page will update automatically.`
                        : "Reviewers will see AI suggestions when they open the screening queue."}
                    </p>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 font-semibold text-primary mb-1">
                      <Bot className="h-3.5 w-3.5" />
                      AI screening complete
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-muted-foreground">
                      <span>AI suggestions generated:</span>
                      <span className="font-medium text-foreground">{batchAiResult.processed}</span>
                      {batchAiResult.errors > 0 && (
                        <><span>Errors:</span><span className="font-medium text-destructive">{batchAiResult.errors}</span></>
                      )}
                    </div>
                    <p className="text-muted-foreground mt-1">Reviewers will see AI suggestions when they open the screening queue.</p>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <button
          onClick={handleImport}
          disabled={loading || (batchAiRunning && !batchAiResult?.queued)}
          className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
          {loading ? "Importing…" : canRunBatchAi ? "Import + Run AI screening" : "Import"}
        </button>
      </div>

      <div className="mt-6 p-4 border rounded-lg bg-card">
        <h2 className="text-sm font-semibold mb-2">Supported formats</h2>
        <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
          <li><strong>RIS</strong>: exports from PubMed, Embase, CINAHL, Scopus</li>
          <li><strong>BibTeX (.bib)</strong>: exports from Scopus, IEEE Xplore, Cochrane</li>
          <li><strong>CSV</strong>: spreadsheet exports with headers: title, authors, year, journal, doi, abstract</li>
        </ul>
        <p className="text-xs text-muted-foreground mt-3">
          Deduplication runs automatically on DOI → PMID → fuzzy title match (≥85% trigram similarity).
        </p>
      </div>
    </div>
  );
}
